import { Formik } from 'formik';
import React from 'react';
import { Form } from 'react-bootstrap';
import toast from 'react-hot-toast';
import * as yup from 'yup';

import CloseIcon from 'images/close_btn.svg';

import AutoSave from '@components/AutoSave';
import Button from '@components/ButtonLegacy';
import CheckBoxField from '@components/Form/CheckBoxField';
import { useGroupSubscriptionUpdate } from '@hooks';
import useEventTracking from '@hooks/useEventTracking';
import useTeamSubscriptionCancel from '@hooks/useTeamSubscriptionCancel';
import { ClickTarget, click } from '@tracking';
import {
  GroupSubscriptionRecipient,
  GroupSubscriptionType,
} from '@utils/backend-api/groupSubscriptions';
import { GROUP_SUBSCRIPTION_GIFT, GROUP_SUBSCRIPTION_TEAM } from '@utils/constants';

import styles from './styles.module.scss';

const schema = () =>
  yup.object().shape({
    recipientId: yup.number(),
    autoRenew: yup.boolean(),
    email: yup.string(),
  });

interface EditRecipientCardProps {
  recipient: GroupSubscriptionRecipient;
  setEditRecipientId: React.Dispatch<React.SetStateAction<number | null>>;
  groupSubscriptionType: GroupSubscriptionType;
}

const EditRecipientCard = ({
  recipient,
  setEditRecipientId,
  groupSubscriptionType,
}: EditRecipientCardProps) => {
  const { trackEvent } = useEventTracking();
  const { mutate: cancelSubscription, isLoading: isCancelSubscriptionLoading } =
    useTeamSubscriptionCancel();
  const { mutate: updateSubscription, isError } = useGroupSubscriptionUpdate(groupSubscriptionType);

  const handleSubmit = ({
    autoRenew,
    recipientId,
    email,
  }: {
    autoRenew: boolean;
    recipientId: number;
    email: string;
  }) => {
    updateSubscription({
      auto_renew: autoRenew,
      recipient_id: recipientId,
    });
    if (isError) {
      toast.error(`Auto renew status failed to update for ${email}`);
    } else {
      toast.success(`Auto renew status updated for ${email}`);
      trackEvent(click({ clickTarget: ClickTarget.Button, clickText: 'Auto renew subscription' }));
    }
  };

  const onCancelSubscriptionClick = () => {
    if (window.confirm('Are you sure you want to cancel this subscription?')) {
      cancelSubscription({
        recipient_id: recipient.recipientId,
      });
      trackEvent(click({ clickTarget: ClickTarget.Button, clickText: 'Cancel subscription' }));
    }
  };

  return (
    <div className={styles.subscriberEditContainer} key={`${recipient.recipientId}-edit`}>
      <div className={styles.nameContainer} key={`${recipient.email}-edit`}>
        <div className={styles.action}>
          <img
            src={CloseIcon}
            className="cursor-pointer"
            onClick={() => setEditRecipientId(null)}
          />
        </div>
      </div>
      {groupSubscriptionType === GROUP_SUBSCRIPTION_TEAM && (
        <div>
          <Button
            onClick={onCancelSubscriptionClick}
            // className={styles.cancelButton}
            disabled={isCancelSubscriptionLoading}
          >
            Cancel subscription
          </Button>
        </div>
      )}
      {groupSubscriptionType === GROUP_SUBSCRIPTION_GIFT && (
        <Formik
          initialValues={{
            recipientId: recipient.recipientId,
            autoRenew: recipient.autoRenew,
            email: recipient.email,
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {(formik) => {
            return (
              <Form noValidate onSubmit={formik.handleSubmit} className={styles.formContainer}>
                <AutoSave debounceMs={500} />
                <div className={styles.subEmail}>{recipient.email}</div>
                <div className={styles.formWrapper}>
                  <CheckBoxField name="autoRenew" label="Auto Renew Subscription" size="small" />
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default EditRecipientCard;
